import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  PLATFORM_ID,
  computed,
  inject,
  input,
  linkedSignal,
  viewChildren,
} from '@angular/core';

import {
  IInternalDirectoryLinkContext,
  IInternalDirectoryLinkItem,
  INTERNAL_LINK_CITY_INSURANCES,
  INTERNAL_LINK_CONCERNS,
  INTERNAL_LINK_POPULAR_FILTERS,
  INTERNAL_LINK_STATE_CITIES,
  INTERNAL_LINK_STATE_INSURANCES,
  INTERNAL_LINK_TREATMENT_APPROACHES,
  URIPrefix,
  DirectoryLinkService,
  isEmptyUriValue,
} from '@sondermind/data-access/directory-link';
import { IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { USStateAbbrevMap } from '@sondermind/utilities/states-list';
import { titleCase } from '@sondermind/utilities/tools/transformers/title-case-transformer';

enum LinkMode {
  CITY = 'city',
  STATE = 'state',
}

enum TabKey {
  CITY = 'city',
  INSURANCE = 'insurance',
  CONCERN = 'concern',
  TREATMENT = 'treatment',
  POPULAR = 'popular',
}

type DirectoryLinkCard = {
  label: string;
  url: string;
};

type DirectoryLinkTab = {
  key: TabKey;
  label: string;
  sectionHeading: string;
  links: DirectoryLinkCard[];
};

@Component({
  selector: 'internal-link-module',
  imports: [IrisIconComponent],
  templateUrl: './internal-link-module.component.html',
  styleUrls: ['./internal-link-module.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternalLinkModuleComponent {
  readonly stateAbbrev = input<string>('');
  readonly city = input<string | null | undefined>();

  private readonly tabButtons = viewChildren<ElementRef<HTMLButtonElement>>('tabButton');

  protected readonly iconSizes = IconSizes;
  protected readonly linkMode = LinkMode;
  // Sync with nth-child(n + 6) in .scss.
  protected readonly mobileVisibleCount = 5;

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly directoryLinkService = inject(DirectoryLinkService);

  protected readonly isStatePage = computed(() => {
    const city = this.city();
    return !city || isEmptyUriValue(city);
  });

  // Reset per-location UI state — host reuses this instance and swaps `city` in place.
  private readonly locationKey = computed(() => `${this.stateAbbrev()}|${this.city()}`);

  private readonly userMode = linkedSignal<string, LinkMode>({
    source: this.locationKey,
    computation: () => LinkMode.CITY,
  });
  protected readonly mode = computed<LinkMode>(() => (this.isStatePage() ? LinkMode.STATE : this.userMode()));

  private readonly selectedTabKey = linkedSignal<string, TabKey | null>({
    source: this.locationKey,
    computation: () => null,
  });
  protected readonly showAll = linkedSignal<string, boolean>({
    source: this.locationKey,
    computation: () => false,
  });

  protected readonly stateName = computed(() => {
    const abbrev = this.stateAbbrev().toUpperCase();
    return abbrev ? USStateAbbrevMap[abbrev] ?? abbrev : '';
  });

  protected readonly cityName = computed(() => {
    const city = this.city();
    return city && !isEmptyUriValue(city) ? city.split(' ').map(titleCase).join(' ') : '';
  });

  protected readonly heading = computed(() => {
    const location = this.mode() === LinkMode.STATE ? this.stateName() : this.cityName();
    return { prefix: 'Browse top therapist filters in', location };
  });

  protected readonly subheading = computed(() => {
    if (this.mode() === LinkMode.STATE) {
      return 'Jump straight to the most-searched providers across the state — by insurance ' +
        'accepted, area of focus, therapy approach, and specialty.';
    }
    return `Jump straight to the most-searched providers in ${this.cityName()} — by insurance ` +
      'accepted, area of focus, therapy approach, and specialty.';
  });

  protected readonly tabs = computed<DirectoryLinkTab[]>(() => this.buildTabs());

  protected readonly activeTab = computed<DirectoryLinkTab | undefined>(() => {
    const tabs = this.tabs();
    return tabs.find((tab) => tab.key === this.selectedTabKey()) ?? tabs[0];
  });

  protected setMode(mode: LinkMode): void {
    if (this.isStatePage() || this.userMode() === mode) return;
    this.userMode.set(mode);
    this.selectedTabKey.set(null);
    this.showAll.set(false);
  }

  protected selectTab(key: TabKey): void {
    if (this.selectedTabKey() === key) return;
    this.selectedTabKey.set(key);
    this.showAll.set(false);
  }

  protected onTabKeydown(event: KeyboardEvent, index: number): void {
    const tabs = this.tabs();
    const lastIndex = tabs.length - 1;
    if (lastIndex < 0) return;

    let target: number;
    switch (event.key) {
      case 'ArrowRight':
        target = index === lastIndex ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        target = index === 0 ? lastIndex : index - 1;
        break;
      case 'Home':
        target = 0;
        break;
      case 'End':
        target = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.selectTab(tabs[target].key);
    this.tabButtons()[target]?.nativeElement.focus();
  }

  protected toggleShowAll(anchor: HTMLElement): void {
    const willCollapse = this.showAll();
    this.showAll.update((value) => !value);
    if (willCollapse && this.isBrowser) {
      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private buildTabs(): DirectoryLinkTab[] {
    const mode = this.mode();
    const location = mode === LinkMode.STATE ? this.stateName() : this.cityName();
    const tabs: DirectoryLinkTab[] = [];

    if (mode === LinkMode.STATE) {
      tabs.push({
        key: TabKey.CITY,
        label: 'By city',
        sectionHeading: `Therapists by city in ${location}`,
        links: this.buildCityLinks(),
      });
    }

    tabs.push({
      key: TabKey.INSURANCE,
      label: 'By insurance',
      sectionHeading: `Therapists by insurance in ${location}`,
      links: this.buildInsuranceLinks(mode),
    });
    tabs.push(
      this.buildGroupTab(TabKey.CONCERN, 'By concern', INTERNAL_LINK_CONCERNS.items, `concern in ${location}`)
    );
    tabs.push(
      this.buildGroupTab(TabKey.TREATMENT, 'By treatment approach', INTERNAL_LINK_TREATMENT_APPROACHES.items,
        `treatment approach in ${location}`)
    );
    tabs.push(
      this.buildGroupTab(TabKey.POPULAR, 'Popular filters', INTERNAL_LINK_POPULAR_FILTERS.items,
        `popular filter in ${location}`)
    );

    return tabs.filter((tab) => tab.links.length > 0);
  }

  private buildInsuranceLinks(mode: LinkMode): DirectoryLinkCard[] {
    const stateSlug = this.stateAbbrev().toLowerCase();
    const titles =
      mode === LinkMode.STATE
        ? INTERNAL_LINK_STATE_INSURANCES[stateSlug] ?? []
        : this.cityInsuranceTitles(stateSlug);
    return titles.map((title) => this.toCard({ label: title, prefix: URIPrefix.INSURANCE }));
  }

  private cityInsuranceTitles(stateSlug: string): string[] {
    const byCity = INTERNAL_LINK_CITY_INSURANCES[stateSlug];
    if (!byCity) return [];
    const target = this.citySlug();
    const cityKey = Object.keys(byCity).find((name) => name.toLowerCase() === target);
    return cityKey ? byCity[cityKey] : [];
  }

  private buildCityLinks(): DirectoryLinkCard[] {
    const cities = INTERNAL_LINK_STATE_CITIES[this.stateAbbrev().toLowerCase()] ?? [];
    return cities.map((name) => this.toCard({ label: name, prefix: URIPrefix.CITY }));
  }

  private buildGroupTab(
    key: TabKey,
    label: string,
    items: IInternalDirectoryLinkItem[],
    headingSuffix: string
  ): DirectoryLinkTab {
    return {
      key,
      label,
      sectionHeading: `Therapists by ${headingSuffix}`,
      links: items.map((item) => this.toCard(item)),
    };
  }

  private toCard(item: IInternalDirectoryLinkItem): DirectoryLinkCard {
    return {
      label: item.isProviderType ? item.label : `${item.label} therapists`,
      url: this.directoryLinkService.buildInternalDirectoryLink(item, this.linkContext()),
    };
  }

  private linkContext(): IInternalDirectoryLinkContext {
    const stateAbbreviation = this.stateAbbrev();
    return this.mode() === LinkMode.STATE
      ? { stateAbbreviation }
      : { stateAbbreviation, city: this.city() ?? undefined };
  }

  private citySlug(): string {
    return (this.city() ?? '').toLowerCase();
  }
}
